<!-- ChronicCareManagement.vue -->
<script setup lang="ts">
import "../../../../css/simpleFlexTable.css"
import { DisplayStyle, MessageSectionType, DateDisplayFormat } from "../../../Enums/UiEnums"
import { initPinia } from "../../../lib/PiniaMgr";
import { useListStore } from "../../../lib/Stores/ListStore";
import SimpleFlexTable from "../../../components/Table/SimpleFlexTable.vue";
import SimpleFlexTableTitledRow from "../../../components/Table/SimpleFlexTableTitledRow.vue"
import DatePickerNoTZ from "../../../components/DatePickerNoTZ.vue";
import TextInput from "../../../components/TextInput.vue";
import MessageSection from "../../../components/Messages/MessageSection.vue";
import ListSelect from "../../../components/Select/ListSelect.vue";
import PhoneInput from "../../../components/Inputs/PhoneInput.vue";
import CheckboxMapYN from "../../../components/Inputs/CheckboxMapYN.vue";
import { IPatientDecodeValue } from "../../../lib/Models/Patient/IPatientDecodeValue";

    const props = defineProps<{
        patientData: IPatientDecodeValue
    }>();


    // Initialize list store data
    const listStore = useListStore(initPinia());    
    listStore.getRelationships_ResponsibleParty();
    listStore.getTimeOfDay_ChronicCareManagement();
    listStore.getChronicCareManagement_NewsLetterDeliveryMethods();

    const emits = defineEmits(["update:patientData"]);
    function emitPatientData()
    {
        emits("update:patientData", props.patientData);
    }
</script>

<style>
    <!-- .titleCol { flex: 0 0 250px; }     -->
    .TitleColWidth { flex-basis: 12em; flex-grow: 0; }
</style>


<template>
    <template v-if="patientData">
        <SimpleFlexTable :columnClasses='["simple-cell TitleColWidth", "simple-cell"]'>
            <SimpleFlexTableTitledRow>
                <template #Title>Ordered</template>
                <DatePickerNoTZ v-model:value="patientData.providerConsentDate" @update:value="emitPatientData" :internalDataFormat="DateDisplayFormat.ISO8601" :disabled="true"/>
            </SimpleFlexTableTitledRow>
    
            <SimpleFlexTableTitledRow>
                <template #Title>Explain &amp; Offer CMM</template>
                <DatePickerNoTZ v-model:value="patientData.ccmOfferDate" @update:value="emitPatientData" :internalDataFormat="DateDisplayFormat.ISO8601" />
            </SimpleFlexTableTitledRow>
    
            <SimpleFlexTableTitledRow>
                <template #Title>Verbal Consent</template>
                <DatePickerNoTZ v-model:value="patientData.verbalConsentDate" @update:value="emitPatientData" :internalDataFormat="DateDisplayFormat.ISO8601" />
            </SimpleFlexTableTitledRow>
    
            <SimpleFlexTableTitledRow>
                <template #Title>Written Consent</template>
                <DatePickerNoTZ v-model:value="patientData.writtenConsentDate" @update:value="emitPatientData" :internalDataFormat="DateDisplayFormat.ISO8601" />
            </SimpleFlexTableTitledRow>
    
            <SimpleFlexTableTitledRow>
                <template #Title>Discontinuation</template>
                <DatePickerNoTZ v-model:value="patientData.verbalRevokeDate" @update:value="emitPatientData" :internalDataFormat="DateDisplayFormat.ISO8601" />
            </SimpleFlexTableTitledRow>

            <SimpleFlexTableTitledRow>
                <template #Title>Contact</template>
                <TextInput v-model:textValue="patientData.ccmContact" @update:textValue="emitPatientData" :maxLength="95" />
            </SimpleFlexTableTitledRow>
    
            <SimpleFlexTableTitledRow>
                <template #Title>Contact Phone</template>
                <PhoneInput v-model="patientData.ccmPhone" @update:modelValue="emitPatientData" />
            </SimpleFlexTableTitledRow>
            
            <SimpleFlexTableTitledRow>
                <template #Title>Contact Relationship</template>
                <ListSelect 
                    v-model="patientData.ccmRelationship" 
                    @update:modelValue="emitPatientData"
                    :options="listStore.relationships_ResponsibleParty"  />
            </SimpleFlexTableTitledRow>
    
            <SimpleFlexTableTitledRow>
                <template #Title>Contact Time</template>

                <ListSelect 
                    v-model="patientData.ccmTod" 
                    @update:modelValue="emitPatientData"
                    :options="listStore.timeOfDay_ChronicCareManagement"  />

                <CheckboxMapYN v-model="patientData.ccmSunday" @update:modelValue="emitPatientData" class="me-3 ms-1">Sun</CheckboxMapYN>
                <CheckboxMapYN v-model="patientData.ccmMonday" @update:modelValue="emitPatientData" class="me-3">Mon</CheckboxMapYN>
                <CheckboxMapYN v-model="patientData.ccmTuesday" @update:modelValue="emitPatientData" class="me-3">Tue</CheckboxMapYN>
                <CheckboxMapYN v-model="patientData.ccmWednesday" @update:modelValue="emitPatientData" class="me-3">Wed</CheckboxMapYN>
                <CheckboxMapYN v-model="patientData.ccmThursday" @update:modelValue="emitPatientData" class="me-3">Thu</CheckboxMapYN>
                <CheckboxMapYN v-model="patientData.ccmFriday" @update:modelValue="emitPatientData" class="me-3">Fri</CheckboxMapYN>
                <CheckboxMapYN v-model="patientData.ccmSaturday" @update:modelValue="emitPatientData" class="me-3">Sat</CheckboxMapYN>
            </SimpleFlexTableTitledRow>        
    
            <SimpleFlexTableTitledRow>
                <template #Title>News Letter</template>

                <ListSelect 
                    v-model="patientData.ccmNewsLetter" 
                    @update:modelValue="emitPatientData"
                    :options="listStore.chronicCareManagement_NewsLetterDeliveryMethods" />
            </SimpleFlexTableTitledRow>
    
            <SimpleFlexTableTitledRow>
                <template #Title>Insurance</template>
                <textarea rows="6" v-model="patientData.ccmInsuranceList" 
                    @update:modelValue="emitPatientData"
                    disabled class="form-control" />
                <CheckboxMapYN
                    v-model="patientData.ccmSecondaryInsFlag"
                    @update:modelValue="emitPatientData"
                    trueVal="Y" falseVal="N">
                    Has Secondary
                </CheckboxMapYN>
            </SimpleFlexTableTitledRow>
    
        </SimpleFlexTable>
    </template>
    <template v-if="!patientData">
        <MessageSection :type="MessageSectionType.Row" :displayStyle="DisplayStyle.Danger">
            Patient Data is not Available
        </MessageSection>
    </template>
</template>